import pictogramAsset from "assets/img/pictogram/asset.svg"
import IconTrashBin from "assets/img/icon-trash-bin.svg?react"
import iconLoadingUnloading from "assets/img/icon-loading-unloading.svg"

import { FC, MouseEvent, ReactNode, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { IUpdateShippingOrderCartAssetDto } from "models/ShippingOrderCartAsset"
import Table, { TableRow } from "components/Table/Table"
import { Grid } from "components/Grid"
import Pictogram from "components/Pictogram/Pictogram"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import Lmid from "components/Lmid/Lmid"
import NoValue from "components/NoValue/NoValue"
import Button from "components/Button/Button"
import { Guid } from "@/types"
import articleSidebars from "stores/articleSidebars"
import AssetStoredStatus from "components/AssetStoredStatus/AssetStoredStatus"
import Select, { ISelectOption } from "components/Select/Select"
import RequestLocation, { ICreateRequestLocation, RequestLocationType } from "models/RequestLocation"
import ShippingOrderAssetExtended from "models/ShippingOrderAssetExtended"
import { IShippingOrderAssetsSort } from "./config"
import useShippingOrderAssetsTable from "./hooks/useShippingOrderAssetsTable"
import ShippingOrderLocationItem from "./ShippingOrderLocationItem"
import { IShippingOrderAddLocationModalProps } from "./ShippingOrderAddLocationModal"
import useSelectStartReset from "hooks/useSelectStartReset"

interface IShippingOrderModalAssetsProps {
    onDeleteAssetClick?(assetId: Guid): void
    createRequestLocation?(assetIds: Guid[], isUnloading: boolean, data: ICreateRequestLocation): Promise<void>
    changeAssetDepartureArrivalLocation?(assetIds: Guid[], changes: Pick<IUpdateShippingOrderCartAssetDto, "departureLocationId" | "arrivalLocationId">): Promise<void>
    isDisabled?: boolean
    isSubmitted?: boolean
    locations: RequestLocation[] | null
    setAddLocationParams: (params: IShippingOrderAddLocationModalProps["params"]) => void
    sort: IShippingOrderAssetsSort | null
    setSort: (sort: IShippingOrderAssetsSort | null) => void
    assets: ShippingOrderAssetExtended[]
    isLoadingAssetDisplayStatuses: boolean
    selectedAssetIds: Guid[]
    onAssetSelectionChange(selectedAssetIds: Guid[]): void
}

enum RequestLocationSelectGroup {
    requestLocations = "requestLocations",
    warehouses = "warehouses",
}

const ShippingOrderModalAssets: FC<IShippingOrderModalAssetsProps> = ({
    onDeleteAssetClick,
    createRequestLocation,
    changeAssetDepartureArrivalLocation,
    isDisabled,
    isSubmitted,
    locations,
    setAddLocationParams,
    sort,
    setSort,
    assets,
    isLoadingAssetDisplayStatuses,
    selectedAssetIds,
    onAssetSelectionChange,
}) => {
    const { t } = useTranslation()
    useSelectStartReset()
    const {
        tableColumns,
    } = useShippingOrderAssetsTable({
        isLoadingAssetDisplayStatuses,
        actionsColumn: !!onDeleteAssetClick,
        sort,
        setSort,
    })

    const selectedRowIndexes = useMemo(() => {
        return selectedAssetIds.reduce((acc, assetId) => {
            const index = assets.findIndex(a => a.assetId === assetId)
            if (index > -1) {
                acc.push(index)
            }
            return acc
        }, [] as number[])
    }, [assets, selectedAssetIds])

    const requestLocationsSelectOptions: ISelectOption<Guid>[] | undefined = useMemo(() => {
        if (!locations) return
        const options: ISelectOption<Guid>[] = [
            ...locations.map(rl => ({
                value: rl.id,
                label: rl.description,
                group: rl.type === RequestLocationType.warehouse ? RequestLocationSelectGroup.warehouses : RequestLocationSelectGroup.requestLocations,
            })) as ISelectOption<Guid>[],
            ...createRequestLocation ? [{
                value: "",
                label: t("shippingOrders.addRequestLocation"),
                group: RequestLocationSelectGroup.requestLocations,
                hasExternalAction: true,
            }] as ISelectOption<Guid>[] : []
        ]
        return options
    }, [createRequestLocation, locations, t])

    const renderRequestLocationSelectOption = useCallback((option: ISelectOption<Guid>, search: string) => {
        return <ShippingOrderLocationItem
            locations={locations}
            locationId={option.value}
            label={option.label}
            highlight={search}
        />
    }, [locations])

    const handleLocationChange = useCallback((assetId: Guid, isUnloading: boolean, changes: Pick<IUpdateShippingOrderCartAssetDto, "departureLocationId" | "arrivalLocationId">) => {
        if (!changes.departureLocationId && !changes.arrivalLocationId) {
            setAddLocationParams({ assetIds: [assetId], isUnloading })
            return
        }
        changeAssetDepartureArrivalLocation?.([assetId], changes)
    }, [changeAssetDepartureArrivalLocation, setAddLocationParams])

    const getSelectGroupHeader = useCallback((group: RequestLocationSelectGroup) => {
        if (group === RequestLocationSelectGroup.requestLocations) {
            return t("shippingOrders.requestLocations")
        }
        if (group === RequestLocationSelectGroup.warehouses) {
            return t("shippingOrders.warehouses")
        }
    }, [t])

    const handleRowClick = useCallback((index: number, event: MouseEvent) => {
        const clickedAssetId = assets[index].assetId

        if (event.ctrlKey || event.metaKey) {
            const newSelectedAssetIds = selectedAssetIds.includes(clickedAssetId)
                ? selectedAssetIds.filter(id => id !== clickedAssetId)
                : [...selectedAssetIds, clickedAssetId]
            onAssetSelectionChange(newSelectedAssetIds)
            return
        }

        if (event.shiftKey) {
            document.getSelection()?.removeAllRanges()
            const prevSelectedIndex = selectedRowIndexes.length > 0 ? selectedRowIndexes[selectedRowIndexes.length - 1] : -1
            if (prevSelectedIndex === -1) {
                onAssetSelectionChange([clickedAssetId])
                return
            }
            const start = Math.min(prevSelectedIndex, index)
            const end = Math.max(prevSelectedIndex, index)
            const newSelectedAssetIds = assets.slice(start, end + 1).map(a => a.assetId)
            onAssetSelectionChange([...selectedAssetIds, ...newSelectedAssetIds])
            return
        }

        onAssetSelectionChange([clickedAssetId])
    }, [assets, onAssetSelectionChange, selectedAssetIds, selectedRowIndexes])

    const renderRequestLocationSelect = useCallback(({
        assetId,
        locationId,
        placeholder,
        isUnloading,
    }: {
        assetId: Guid
        locationId?: Guid
        placeholder: string
        isUnloading?: boolean
    }) => {
        return (
            <>
                <Select
                    options={requestLocationsSelectOptions}
                    value={requestLocationsSelectOptions ? locationId : undefined}
                    placeholder={requestLocationsSelectOptions ? placeholder : t("common.loading")}
                    isDisabled={isDisabled || !requestLocationsSelectOptions}
                    isInvalid={isSubmitted && !locationId}
                    hideUnknownValue
                    optionTemplate={renderRequestLocationSelectOption}
                    onChange={option => handleLocationChange(assetId, isUnloading || false, isUnloading ? { arrivalLocationId: option?.value } : { departureLocationId: option?.value })}
                    menuWidth="28rem"
                    groupOptions={options => {
                        return Object.values(RequestLocationSelectGroup).map(group => (
                            {
                                label: getSelectGroupHeader(group),
                                options: options.filter(option => option.group === group),
                            }
                        ))
                    }}
                    isInModal
                />
            </>
        )
    }, [requestLocationsSelectOptions, t, isDisabled, renderRequestLocationSelectOption, isSubmitted, handleLocationChange, getSelectGroupHeader])

    const tableRows = useMemo<TableRow[]>(() => {
        return assets.map(item => {
            const columns: ReactNode[] = [
                <button type="button"
                    onClick={e => {
                        e.stopPropagation()
                        if (!item.articleId) return
                        articleSidebars.show({ articleId: item.articleId })
                    }}
                    className="link d-block color-brand-blue">
                    <Grid columns="max-content minmax(0, auto)"
                        gap="1.6rem"
                        alignItems="center">
                        <Pictogram
                            src={pictogramAsset}
                            size="4rem"
                            imgSize="2.2rem"
                        />
                        <FlexibleContent text={item.altArticleDescription || item.articleDescription} />
                    </Grid>
                </button>,
                <Lmid
                    lmid={item.lmid}
                    assetId={item.assetId}
                    labeled={item.isLabeled}
                />,
                item.displayStatus
                    ? <Grid columns="max-content minmax(0, auto)" gap="1rem" alignItems="center">
                        <AssetStoredStatus status={item.displayStatus.status} />
                        <FlexibleContent text={item.displayStatus.locationName || ""} />
                    </Grid>
                    : <NoValue>{t("common.loading")}</NoValue>,
                changeAssetDepartureArrivalLocation
                    ? renderRequestLocationSelect({
                        assetId: item.assetId,
                        locationId: item.departureLocationId,
                        placeholder: t("shippingOrders.fromLocation"),
                    })
                    : <ShippingOrderLocationItem
                        locations={locations}
                        locationId={item.departureLocationId}
                        label={locations?.find(loc => loc.id === item.departureLocationId)?.description || ""}
                    />,
                <img src={iconLoadingUnloading} alt="" />,
                changeAssetDepartureArrivalLocation
                    ? renderRequestLocationSelect({
                        assetId: item.assetId,
                        locationId: item.arrivalLocationId,
                        placeholder: t("shippingOrders.toLocation"),
                        isUnloading: true,
                    })
                    : <ShippingOrderLocationItem
                        locations={locations}
                        locationId={item.arrivalLocationId}
                        label={locations?.find(loc => loc.id === item.arrivalLocationId)?.description || ""}
                    />,
                item.startDate?.format(t("config.dateLongFormat")) || <NoValue />,
                item.endDate?.format(t("config.dateLongFormat")) || <NoValue />,
            ]
            if (onDeleteAssetClick) {
                columns.push(
                    <Button
                        icon={<IconTrashBin />}
                        theme="grey"
                        round
                        disabled={isDisabled}
                        onClick={e => {
                            e.stopPropagation()
                            onDeleteAssetClick(item.assetId)
                        }}
                    />
                )
            }
            return columns
        })
    }, [assets, changeAssetDepartureArrivalLocation, isDisabled, locations, onDeleteAssetClick, renderRequestLocationSelect, t])

    return (
        <Table
            columns={tableColumns}
            rows={tableRows}
            highlightRowIndexes={selectedRowIndexes}
            highlightRowStyle="grey"
            hoverHighlightRowStyle="grey"
            onRowClick={handleRowClick}
            theme="border-between"
            clampHeaders={2}
            stickyHeader
            condensed
        />
    )
}

export default ShippingOrderModalAssets
